import { useCallback, useContext, useEffect, useRef, useState } from "react";
import shortid from "shortid";

import { DragDropContext } from "./DragDropProvider";

let droppables = {};
let currentElement = null;

export const useDrag = (props) => {
  const [state, dispatch] = useContext(DragDropContext);
  const [id] = useState(props.draggableId || shortid.generate());
  const [dragInstance, setDragInstance] = useState();
  const ref = useRef();

  /** One-time registration of the draggable. */
  useEffect(() => {
    dispatch({
      type: "add_draggable",
      data: {
        id: id,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const testIsInsideDroppable = useCallback(
    (x, y) => {
      if (state.draggable) {
        let dragElement = dragInstance;
        dragElement.style.display = "none";
        dragElement.hidden = true;
        let elemBelow = document.elementFromPoint(x, y);
        dragElement.style.display = "inline-block";
        dragElement.hidden = false;

        if (elemBelow !== currentElement) {
          let hit = null;
          Object.keys(droppables).forEach((k) => {
            if (droppables[k].current === elemBelow) {
              hit = k;
            }
          });
          currentElement = elemBelow;
          return hit;
        }
      }

      return state.droppableId !== null ? state.droppableId : null;
    },
    [dragInstance, state.draggable, state.droppableId],
  );

  const handleMouseMove = useCallback(
    ({ pageX, pageY, touches }) => {
      let dragData = {
        x: pageX || touches[0].pageX,
        y: pageY || touches[0].pageY,
      };

      // Get coordinates of draggable relative to droppable
      if (state.droppableId) {
        const rect =
          droppables[state.droppableId].current.getBoundingClientRect();
        dragData.droppableCoords = {
          x: dragData.x - rect.x,
          y: dragData.y - rect.y,
        };
      }

      dispatch({
        type: "drag",
        data: dragData,
      });

      if (props.onDrag) {
        props.onDrag(ref, dragData.x, dragData.y);
      }

      // Entering or leaving a droppable
      let droppableId = testIsInsideDroppable(dragData.x, dragData.y);
      if (droppableId !== state.droppableId) {
        if (droppableId) {
          dispatch({
            type: "drag_enter",
            data: {
              dropId: droppableId,
              dragId: id,
            },
          });
        } else {
          dispatch({
            type: "drag_leave",
          });
        }
      }
    },
    [dispatch, id, props, state.droppableId, testIsInsideDroppable],
  );

  /**
   * MouseDown: Handles initial clone of the draggable token (if requested).
   * @param {*} e
   */
  const handleMouseDown = useCallback(
    (e) => {
      // ignore right-clicks
      if (e.button > 0) {
        return;
      }

      e.preventDefault();

      const x = e.pageX || e.touches[0].pageX;
      const y = e.pageY || e.touches[0].pageY;

      let rect = ref.current.getBoundingClientRect();

      // Clone re-usable tokens
      if (props.cloneOnDrag) {
        let clone = ref.current.cloneNode(true);
        const halfW = rect.width / 2;
        const halfH = rect.height / 2;

        document.body.appendChild(clone);
        clone.style.boxShadow = "rgba(0, 0, 0, 0.3) 0px 0px 30px";
        clone.style.position = "absolute";
        // clone.style.margin = 0;
        clone.style.top = y - halfH + "px";
        clone.style.left = x - halfW + "px";
        clone.style.zIndex = 10002;
        clone.ondragstart = function () {
          return false;
        };
        setDragInstance(clone);
      } else {
        setDragInstance(ref.current);
      }

      if (props.onDragStart) {
        props.onDragStart();
      }

      dispatch({
        type: "drag_start",
        data: {
          draggableId: id,
          instanceId: props.instanceId || undefined,
          originalX: x,
          originalY: y,
          width: rect.width,
          height: rect.height,
          offsetX: 0,
          offsetY: 0,
        },
      });
    },
    [dispatch, id, props],
  );

  const handleMouseUp = useCallback(() => {
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
    window.removeEventListener("touchmove", handleMouseMove);
    window.removeEventListener("touchend", handleMouseUp);
    let st = { ...state.draggable };

    if (dragInstance) {
      if (props.cloneOnDrag) {
        dragInstance.remove();
      } else {
        dragInstance.style.transform = null;
      }
    }
    setDragInstance();
    dispatch({
      type: "drag_end",
    });

    if (props.onDragEnd) {
      props.onDragEnd(st);
    }

    currentElement = null;
  }, [dispatch, dragInstance, handleMouseMove, props, state.draggable]);

  // Init: When element becomes available, attach listeners
  useEffect(() => {
    if (ref?.current) {
      // ref.current.style.transition = `box-shadow 0.5s, transform 0.1s`;
      // ref.current.style.boxShadow = "rgba(0, 0, 0, 0) 0px 0px 0px";
      ref.current.addEventListener("mousedown", handleMouseDown);
      ref.current.addEventListener("touchstart", handleMouseDown);

      /** Disable native HTML5 dnd because it sucks. */
      ref.current.ondragstart = function () {
        return false;
      };

      const el = ref.current;
      return () => {
        el.removeEventListener("mousedown", handleMouseDown);
        el.removeEventListener("touchstart", handleMouseDown);
      };
    }
  }, [handleMouseDown, ref]);

  // Effect: Stage changes
  useEffect(() => {
    if (dragInstance) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);

      //touch equivalents
      window.addEventListener("touchmove", handleMouseMove);
      window.addEventListener("touchend", handleMouseUp);

      let dragState = state.draggable;
      if (dragState) {
        dragInstance.style.transform = `translate(${dragState.translateX}px, ${dragState.translateY}px)`;
      }
    }

    return () => {
      if (dragInstance) {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        window.removeEventListener("touchmove", handleMouseMove);
        window.removeEventListener("touchend", handleMouseUp);
      }
    };
  }, [dragInstance, handleMouseMove, handleMouseUp, state]);

  return ref;
};

/**
 *
 * @param {*} props
 * @deprecated
 */
export const useDrop = (props) => {
  const [state, dispatch] = useContext(DragDropContext);
  const [id] = useState(props.id);
  const ref = useRef(null);

  /** One-time registration of the droppable */
  useEffect(() => {
    droppables[id] = ref;

    dispatch({
      type: "add_droppable",
      data: {
        id: id,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Apply stylings for when draggable has entered/left the droppable */
  useEffect(() => {
    if (ref?.current?.classList) {
      if (state.droppableId === id) {
        ref.current.classList.add("is-drag-hover");
      } else {
        ref.current.classList.remove("is-drag-hover");
      }
    }
  }, [id, state]);

  return ref;
};
